import { Hidden, Modal } from '@mui/material'
import IonIcon from '@reacticons/ionicons'
import moment from 'moment'
import { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import { EventFooter } from 'src/components/EventFooter/EventFooter'
// import { EventHeader } from 'src/components/EventHeader/EventHeader'
import { EventPagination } from 'src/components/EventPagination/EventPagination'
import { Footer } from 'src/components/Footer/Footer'
// import { Breadcrumb } from 'src/components/global/Breadcrumb'
import { Filter } from 'src/components/global/filter'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
// import { SearchField } from 'src/components/global/SearchField'
// import { Sidebar } from 'src/components/global/Sidebar'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
// import { Monthcalendar } from 'src/components/Monthcalendar/Monthcalendar'
import SkeletonLoading from 'src/components/SkeletonLoading/SkeletonLoading'
import { THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { IEvent, useSearchEventsLazyQuery } from 'src/graphql/generated/hooks'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption, TaxonomyType } from 'src/types'

const Breadcrumb = dynamic(() => import('src/components/global/Breadcrumb').then((mod) => mod.Breadcrumb), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const EventHeader = dynamic(() => import('src/components/EventHeader/EventHeader').then((mod) => mod.EventHeader), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const Sidebar = dynamic(() => import('src/components/global/Sidebar').then((mod) => mod.Sidebar), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const SearchField = dynamic(() => import('src/components/global/SearchField').then((mod) => mod.SearchField), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const Monthcalendar = dynamic(
  () => import('src/components/Monthcalendar/Monthcalendar').then((mod) => mod.Monthcalendar),
  {
    ssr: false,
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)

type Props = {
  events: IEvent[]
  filters: FilterOption[]
}

type EventProps = {
  filteredEvents: IEvent[]
  filteredEventsForMonth: IEvent[]
  totalCount: number
}

const Events: NextPage<Props> = (props) => {
  const [isListView, setIsListView] = useState(false)

  const router = useRouter()
  const [postsPerPage] = useState(25)
  const [curentMonth, setCurrentMonth] = useState(moment().format('YYYY-MM'))
  const [showFilterDlg, setShowFilterDlg] = useState(false)

  const [loadPageEvents, { loading: pageEventsLoading, data: pageEventsData }] = useSearchEventsLazyQuery()
  const [loadMonthEvents, { loading: monthEventsLoading, data: monthEventData }] = useSearchEventsLazyQuery()

  const events = useMemo<EventProps>(() => {
    if (pageEventsData && monthEventData) {
      const eventsProps: EventProps = {
        filteredEvents: pageEventsData.eventResults.data?.events?.map((item) => item as unknown as IEvent) || [],
        filteredEventsForMonth:
          monthEventData.eventResults.data?.events?.map((item) => item as unknown as IEvent) || [],
        totalCount: pageEventsData.eventResults.data?.total || 0,
      }
      return eventsProps
    }
    return { filteredEvents: [], filteredEventsForMonth: [], totalCount: 0 }
  }, [pageEventsData, monthEventData])

  const loading = useMemo<boolean>(
    () => pageEventsLoading || monthEventsLoading,
    [pageEventsLoading, monthEventsLoading],
  )

  useEffect(() => {
    const curPage = router.query.page !== undefined ? parseInt(router.query.page as string) : 1
    const indexOfLastPost = curPage * postsPerPage
    const indexOfFirstPost = indexOfLastPost - postsPerPage

    const filters = router.query
    const countries = filters !== undefined ? (filters.locations as string) : undefined
    const organizers = filters !== undefined ? (filters.organizations as string) : undefined

    let arrCountries: string[] = []
    const arrConvertedCountries: string[] = []
    if (countries !== undefined) {
      arrCountries = countries.split(',')
      arrCountries.map((country: string) => {
        arrConvertedCountries.push(country.split('-').join(','))
      })
    }

    let arrOrganizers: string[] = []
    const arrConvertedOrganizers: string[] = []
    if (organizers !== undefined) {
      arrOrganizers = organizers.split(',')
      arrOrganizers.map((or: string) => {
        arrConvertedOrganizers.push(or.split('-').join(','))
      })
    }

    const queryFilters = {
      formats: filters !== undefined ? (filters.formats as string) : undefined,
      audiences: filters !== undefined ? (filters.audiences as string) : undefined,
      durations: filters !== undefined ? (filters.durations as string) : undefined,
      features: filters !== undefined ? (filters.attributes as string) : undefined,
      keyword: filters !== undefined ? (filters.keyword as string) : undefined,
      countries:
        filters !== undefined && arrConvertedCountries.length > 0 ? (arrConvertedCountries as string[]) : undefined,
      organizers:
        filters !== undefined && arrConvertedOrganizers.length > 0 ? (arrConvertedOrganizers as string[]) : undefined,
    }

    loadPageEvents({ variables: { query: { ...queryFilters, offset: indexOfFirstPost, limit: postsPerPage } } })
    loadMonthEvents({ variables: { query: { ...queryFilters, date: curentMonth, limit: 25 } } })
  }, [router, curentMonth])

  const handleChangeMode = () => setIsListView(!isListView)
  const onSelectMonth = (date: string) => setCurrentMonth(date)

  const meta = <Meta title="Events" description="Events" url="/events/" />

  return (
    <Main meta={meta}>
      <MainContainer id="mainContainer">
        <div className="flex flex-col bg-clouds">
          <Breadcrumb subject="EVENTS" />
          <EventHeader />
        </div>
        <hr />
        <div className="flex flex-row justify-center w-full">
          <div className="w-full px-5">
            <div className="flex flex-row justify-center w-full">
              <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
                <Hidden mdDown>
                  <div className="flex flex-col md:w-[240px] w-full md:mr-10 md:mt-[0px] mt-[32px] pl-[1.25rem] pr-[1.25rem]">
                    <SearchField className="my-8" />
                    <Sidebar filterOptions={props.filters} />
                  </div>
                </Hidden>
                <div className="flex flex-col w-full">
                  <div className="flex flex-row pt-5 md:pt-0">
                    <Hidden mdUp>
                      <div className="flex flex-col w-full md:pt-[10px]">
                        <Filter onClickFilter={() => setShowFilterDlg(true)} />
                        <div className="font-primary text-[12px] text-darkgray h-[20px] mb-[20px] mt-[3px]">
                          {(events.totalCount ?? 0) > 0
                            ? `${events.totalCount ?? 0} Solution${(events.totalCount ?? 0) > 1 ? 's' : ''} Found`
                            : `No solution found`}
                        </div>
                      </div>
                    </Hidden>

                    <div className="flex flex-row items-start md:items-center md:h-28 ml-auto">
                      {isListView ? (
                        <div
                          className="relative ml-auto cursor-pointer w-[170px] py-2 pl-3 pr-10 border-mist border-solid border rounded-[20px] text-left min-h-[42px] bg-white"
                          onClick={handleChangeMode}
                        >
                          <span className="absolute inset-y-0 flex items-center pr-2 pointer-events-none">
                            <Image src="/calendar-ico.png" width={17} height={17} alt="" />
                          </span>
                          <span className="truncate ml-[30px]">
                            <b>Calendar View</b>
                          </span>
                        </div>
                      ) : (
                        // <div
                        //   className="relative ml-auto cursor-pointer w-[170px] py-2 pl-3 pr-10 border-mist border-solid border rounded-[20px] text-left min-h-[42px] bg-white z-10"
                        //   onClick={handleChangeMode}
                        // >
                        //   <span className="absolute inset-y-0 flex items-center pr-2 pointer-events-none">
                        //     <Image src="/list-ico.png" width={17} height={17} alt="" />
                        //   </span>
                        //   <span className="truncate ml-[30px]">
                        //     <b>List View</b>
                        //   </span>
                        // </div>
                        ''
                      )}
                    </div>
                  </div>

                  {isListView ? (
                    <EventPagination events={events.filteredEvents} totalCount={events.totalCount} loading={loading} />
                  ) : (
                    <div className="flex md:mt-[-75px]">
                      <Monthcalendar events={events.filteredEventsForMonth} onSelectMonth={onSelectMonth} />
                    </div>
                  )}
                  <Modal open={showFilterDlg}>
                    <div className="flex flex-col w-full h-full bg-white p-[17px]">
                      <div className="flex flex-row mb-[13px]">
                        <p className="font-secondary font-bold text-[20px] text-darkgray items-center">Filter</p>
                        <div className="ml-auto" onClick={() => setShowFilterDlg(false)}>
                          <IonIcon name="close-outline" className="!h-[30px] !w-[30px] text-[black]" />
                        </div>
                      </div>
                      <SearchField className="mb-[23px]" />
                      <Sidebar filterOptions={props.filters} />
                      <button
                        className="text-white font-secondary text-[14px] font-bold text-center rounded-[22.5px] px-[25px] bg-navy mt-auto mb-[15px] h-[50px] !important"
                        onClick={() => setShowFilterDlg(false)}
                      >
                        Apply Filters
                      </button>
                    </div>
                  </Modal>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col bg-clouds">
          <EventFooter />
        </div>
        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export default Events

export const getStaticProps: GetStaticProps = async () => {
  if (THEME === 'ilta') return { revalidate: 60, notFound: true }
  try {
    const [{ events }, { eventFormats }, { demographics }, { durations }, { eventFeatures }] = await Promise.all([
      apolloSdk.events(),
      apolloSdk.getFormats(),
      apolloSdk.getAudiences(),
      apolloSdk.getDurations(),
      apolloSdk.eventFeatures(),
    ])
    const { location } = await apolloSdk.getLocations()
    const { organization } = await apolloSdk.getOrganization()

    const filters = [
      {
        title: TaxonomyType.Format,
        data: eventFormats.map((format) => ({ id: format?.documentId || '', name: format?.name })),
      },
      {
        title: TaxonomyType.Location,
        data: location?.map((loc) => ({ id: loc, name: loc })),
      },
      {
        title: TaxonomyType.Organization,
        data: organization?.map((or) => ({ id: or, name: or })),
      },
      {
        title: TaxonomyType.Audience,
        data: demographics.map((item) => ({ id: item?.documentId || '', name: item?.name })),
      },
      {
        title: TaxonomyType.Duration,
        data: durations.map((item) => ({ id: item?.documentId || '', name: item?.name || '' })),
      },
      {
        title: TaxonomyType.EventFeature,
        data: eventFeatures.map((item) => ({ id: item?.documentId || '', name: item?.name })),
      },
    ]

    const eventsData = events || []
    return {
      props: {
        events: eventsData,
        filters,
      },
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error occurred in getStaticProps of Events:', error)
    // Graceful fallback in case of an upstream error
    return {
      notFound: true,
      revalidate: 5, // Reattempt fetching during the next static regeneration
    }
  }
}
